import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button, Card, CardHeader, Input } from '@/components/common';
import api, { getErrorMessage } from '@/services/api';

/**
 * Settings → Password → two-factor authentication (decision 18).
 *
 * TOTP only: no delivery channel on this platform works, so an SMS or e-mail
 * code would be a step nobody could finish. Optional per user; the QR image is
 * rendered by the server, and the backup codes are shown exactly once because
 * the server keeps only their hashes.
 */

interface TwoFactorStatus {
  is_2fa_enabled: boolean;
  enrolment_pending: boolean;
  backup_codes_remaining: number;
}

interface EnrolmentStart {
  provisioning_uri: string;
  secret: string;
  qr_code: string;
  issuer: string;
}

interface CodesResponse extends TwoFactorStatus {
  backup_codes: string[];
}

function BackupCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-500/40 dark:bg-amber-500/10">
      <p className="text-sm font-medium text-gray-900 dark:text-white">
        Save these backup codes now
      </p>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
        Each one signs you in once if you lose your authenticator. They are stored hashed, so this is
        the only time they can be shown.
      </p>
      <ul className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm text-gray-900 dark:text-gray-100">
        {codes.map((code) => (
          <li key={code} className="rounded bg-white px-2 py-1 dark:bg-gray-800">
            {code}
          </li>
        ))}
      </ul>
      <div className="mt-4 flex gap-2">
        <Button
          variant="secondary"
          onClick={() => {
            navigator.clipboard?.writeText(codes.join('\n'));
            toast.success('Backup codes copied');
          }}
        >
          Copy all
        </Button>
        <Button variant="secondary" onClick={onDone}>
          I have saved them
        </Button>
      </div>
    </div>
  );
}

export function TwoFactorPanel() {
  const queryClient = useQueryClient();
  const [enrolment, setEnrolment] = useState<EnrolmentStart | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const {
    data: status,
    isLoading,
    isError,
    error,
  } = useQuery<TwoFactorStatus>({
    queryKey: ['two-factor-status'],
    queryFn: async () => (await api.get('/users/auth/2fa/status/')).data,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['two-factor-status'] });

  const begin = useMutation({
    mutationFn: async () => (await api.post('/users/auth/2fa/begin/', {})).data as EnrolmentStart,
    onSuccess: (data) => {
      setEnrolment(data);
      setCode('');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const confirm = useMutation({
    mutationFn: async () =>
      (await api.post('/users/auth/2fa/confirm/', { code: code.trim() })).data as CodesResponse,
    onSuccess: (data) => {
      setEnrolment(null);
      setCode('');
      setBackupCodes(data.backup_codes);
      refresh();
      toast.success('Two-factor authentication is on');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const disable = useMutation({
    mutationFn: async () => (await api.post('/users/auth/2fa/disable/', { password })).data,
    onSuccess: () => {
      setPassword('');
      setBackupCodes(null);
      refresh();
      toast.success('Two-factor authentication is off');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const regenerate = useMutation({
    mutationFn: async () =>
      (await api.post('/users/auth/2fa/backup-codes/', { password })).data as CodesResponse,
    onSuccess: (data) => {
      setPassword('');
      setBackupCodes(data.backup_codes);
      refresh();
      toast.success('New backup codes issued; the old ones no longer work');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  return (
    <Card className="mt-6">
      <CardHeader
        title="Two-Factor Authentication"
        subtitle="A code from your authenticator app, in addition to your password"
      />

      {isLoading ? (
        <div className="h-20 animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
      ) : isError ? (
        <div className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-500" />
          <span>Could not read your two-factor status. {getErrorMessage(error)}</span>
        </div>
      ) : status?.is_2fa_enabled ? (
        <div className="space-y-4">
          <p className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-400">
            <ShieldCheckIcon className="h-5 w-5" />
            On &mdash; {status.backup_codes_remaining} backup{' '}
            {status.backup_codes_remaining === 1 ? 'code' : 'codes'} left
          </p>
          {backupCodes && <BackupCodes codes={backupCodes} onDone={() => setBackupCodes(null)} />}
          <div className="max-w-md space-y-3">
            <Input
              id="two_factor_password"
              label="Current password"
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              helperText="Needed to change or turn off your second factor."
            />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                disabled={!password || regenerate.isPending}
                isLoading={regenerate.isPending}
                onClick={() => regenerate.mutate()}
              >
                New backup codes
              </Button>
              <Button
                variant="danger"
                disabled={!password || disable.isPending}
                isLoading={disable.isPending}
                onClick={() => disable.mutate()}
              >
                Turn off
              </Button>
            </div>
          </div>
        </div>
      ) : enrolment ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Scan this with Google Authenticator, Authy, 1Password or any TOTP app, then type the
            6-digit code it shows.
          </p>
          <img
            src={enrolment.qr_code}
            alt="Two-factor setup QR code"
            className="h-44 w-44 rounded bg-white p-2"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Can&apos;t scan? Enter this key by hand:{' '}
            <span className="font-mono break-all">{enrolment.secret}</span>
          </p>
          <div className="max-w-xs">
            <Input
              id="two_factor_setup_code"
              label="6-digit code"
              value={code}
              inputMode="numeric"
              autoComplete="one-time-code"
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button
              disabled={!code.trim() || confirm.isPending}
              isLoading={confirm.isPending}
              onClick={() => confirm.mutate()}
            >
              Turn on
            </Button>
            <Button variant="secondary" onClick={() => setEnrolment(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Off. Turning it on means every sign-in asks for a code from your phone as well as your
            password.
          </p>
          {backupCodes && <BackupCodes codes={backupCodes} onDone={() => setBackupCodes(null)} />}
          <Button isLoading={begin.isPending} onClick={() => begin.mutate()}>
            Set up two-factor authentication
          </Button>
        </div>
      )}
    </Card>
  );
}
